import React, { ComponentProps, FC } from 'react'

import { IconProp } from '@fortawesome/fontawesome-svg-core'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

type ButtonSize = 'large' | 'medium' | 'small'
type ButtonVariant = 'primary' | 'secondary' | 'tertiary'

// Types: we can use a FontAwesome icon or a custom React.FC, not both
type BaseKeyButtonProps = ComponentProps<'button'> & {
  size: ButtonSize
  variant: ButtonVariant
  iconWidth?: number
}

type KeyButtonPropsFA = BaseKeyButtonProps & {
  IconFC?: any
  icon?: IconProp
}

type KeyButtonPropsFC = BaseKeyButtonProps & {
  IconFC?: FC<{ width: number; [x: string]: unknown }>
  icon?: never
}

export type KeyButtonProps = KeyButtonPropsFA | KeyButtonPropsFC

const sizing: Record<ButtonSize, string> = {
  large: 'px-4 h-[36px] text-[16px]',
  medium: 'px-4 h-[32px] text-[13px]',
  small: 'px-2.5 h-[24px] text-[10px]',
}

const iconOnlySizing: Record<ButtonSize, string> = {
  large: 'h-9 w-9 text-[16px]',
  medium: 'h-8 w-8 text-[13px]',
  small: 'h-6 w-6 text-[10px]',
}

const bg: Record<ButtonVariant, string> = {
  primary: 'bg-sky-400 hover:bg-sky-400 active:bg-sky-400 focus:bg-sky-400 disabled:bg-gray-400',
  secondary:
    'bg-gray-700 hover:bg-gray-700 active:bg-sky-400 focus:bg-sky-400 disabled:bg-gray-400',
  tertiary: 'bg-sky-300 hover:bg-sky-400 active:bg-sky-400 focus:bg-sky-400 disabled:bg-gray-400',
}

const border: Record<ButtonVariant, string> = {
  primary: 'border-sky-400 disabled:border-0',
  secondary: 'border-sky-400 disabled:border-0',
  tertiary: 'border-sky-400 disabled:border-gray-200',
}

const text: Record<ButtonVariant, string> = {
  primary: 'text-sky-200 disabled:text-gray-400',
  secondary: 'text-sky-200 disabled:text-gray-400',
  tertiary:
    'text-sky-400 hover:text-sky-200 active:text-sky-200 focus:text-sky-200 disabled:text-gray-400',
}

const defaultIconWidth: Record<ButtonSize, number> = {
  large: 20,
  medium: 16,
  small: 12,
}

// KEY Design Language Button | See examples in ops/components

/**
 * @deprecated key-* components are no longer supported.
 */
export const KeyButton: FC<KeyButtonProps> = (props) => {
  const { IconFC, icon, iconWidth, variant, size, children, className, ...buttonProps } = props

  const sizeClasses = !children ? iconOnlySizing[size] : sizing[size]

  const classes = `${bg[variant]} ${text[variant]} ${sizeClasses} ${border[variant]} border rounded font-bold`

  return (
    <button
      {...buttonProps}
      className={`${classes} ${className}`}
    >
      <div className='flex gap-1 items-center justify-center'>
        {IconFC && (
          <div className='shrink-0'>
            <IconFC width={iconWidth || defaultIconWidth[size]} />
          </div>
        )}
        {icon && (
          <div className='shrink-0'>
            <FontAwesomeIcon
              width={iconWidth || defaultIconWidth[size]}
              icon={icon}
            />
          </div>
        )}
        {children && <div className='shrink-0'>{children}</div>}
      </div>
    </button>
  )
}
